import { useCallback, useEffect, useState } from 'react'
import { CellProps } from 'react-table'
import { Button } from '@mantine/core'
import { RouteComponentProps } from 'react-router-dom'
import BaseTable from '../../components/BaseTable/BaseTable'
import DashboardWrapper from '../../components/MainDashboardWrapper/DashboardWrapper'
import { formatDateTime } from '../../helpers/dateUtils'
import { getAutomations, toggleAutomation, duplicateAutomation, deleteAutomation } from './helper'

const TARGET_STATUS_LABELS: Record<string, string> = {
  subscribed: 'Subscribed coaches',
  active: 'Active',
  expiringSoon: 'Expiring soon',
  expired: 'Expired',
  unsubscribed: 'Unsubscribed',
}

const TRIGGER_LABELS: Record<string, (days: number) => string> = {
  expires_in_days: (d) => `Expires in ${d} day${d === 1 ? '' : 's'}`,
  expired_after_days: (d) => `${d} day${d === 1 ? '' : 's'} after expiry`,
  unsubscribed_after_days: (d) => `${d} day${d === 1 ? '' : 's'} after unsubscribing`,
  subscribed_after_days: (d) => `${d} day${d === 1 ? '' : 's'} after subscribing`,
  recurring_every_minute: () => 'Every minute',
  recurring_daily: () => 'Every day',
  recurring_weekly: () => 'Every week',
  recurring_monthly: () => 'Every month',
}

export default function AutomationListPage({ history }: RouteComponentProps) {
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    setLoadError(null)
    getAutomations()
      .then((res) => setData(res.data || []))
      .catch((err) => setLoadError(err || 'Failed to load automations'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const onToggle = (row: any) => {
    const next = row.status === 'enabled' ? 'disabled' : 'enabled'
    toggleAutomation(row.id, next)
      .then(load)
      .catch((err) => window.alert(err || 'Action failed'))
  }

  const onDuplicate = (row: any) => {
    duplicateAutomation(row.id)
      .then(load)
      .catch((err) => window.alert(err || 'Action failed'))
  }

  const onDelete = (row: any) => {
    if (!window.confirm(`Delete automation "${row.name}"? This also deletes its send log.`)) return
    deleteAutomation(row.id)
      .then(load)
      .catch((err) => window.alert(err || 'Action failed'))
  }

  const columns: any[] = [
    { Header: 'Name', accessor: 'name' },
    {
      Header: 'Target Status',
      accessor: 'targetStatus',
      Cell: (cellProps: CellProps<any>) => TARGET_STATUS_LABELS[cellProps.value] || cellProps.value,
    },
    {
      Header: 'Trigger',
      id: 'trigger',
      Cell: (cellProps: CellProps<any>) => {
        const row = cellProps.row.original
        const labelFn = TRIGGER_LABELS[row.triggerType]
        return labelFn ? labelFn(row.triggerDays) : row.triggerType
      },
    },
    {
      Header: 'Last Run',
      accessor: 'lastRunAt',
      Cell: (cellProps: CellProps<any>) => (cellProps.value ? formatDateTime(cellProps.value) : 'Never'),
    },
    { Header: 'Emails Sent', accessor: 'emailsSentCount' },
    {
      Header: 'Open Rate',
      id: 'openRate',
      Cell: (cellProps: CellProps<any>) => {
        const stats = cellProps.row.original.stats
        return stats?.sent ? `${stats.opened}/${stats.sent} (${Math.round(stats.openRate * 100)}%)` : '—'
      },
    },
    {
      Header: 'Click Rate',
      id: 'clickRate',
      Cell: (cellProps: CellProps<any>) => {
        const stats = cellProps.row.original.stats
        return stats?.sent ? `${stats.clicked}/${stats.sent} (${Math.round(stats.clickRate * 100)}%)` : '—'
      },
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: (cellProps: CellProps<any>) => (
        <span
          className="text-xs font-bold px-2.5 py-1 rounded-full"
          style={{
            background: cellProps.value === 'enabled' ? '#EAF7EF' : '#F1F5F9',
            color: cellProps.value === 'enabled' ? '#16A34A' : '#64748B',
          }}
        >
          {cellProps.value === 'enabled' ? 'Enabled' : 'Disabled'}
        </span>
      ),
    },
    {
      Header: 'Actions',
      accessor: 'id',
      Cell: (cellProps: CellProps<any>) => {
        const row = cellProps.row.original
        return (
          <div className="flex gap-2 flex-wrap">
            <Button size="xs" onClick={() => history.push(`/subscriptions/automation/${row.id}/edit`)}>
              Edit
            </Button>
            <Button size="xs" variant="outline" onClick={() => onToggle(row)}>
              {row.status === 'enabled' ? 'Disable' : 'Enable'}
            </Button>
            <Button size="xs" variant="outline" onClick={() => onDuplicate(row)}>
              Duplicate
            </Button>
            <Button size="xs" variant="outline" onClick={() => history.push(`/subscriptions/automation/${row.id}/logs`)}>
              Logs
            </Button>
            <Button size="xs" color="red" variant="outline" onClick={() => onDelete(row)}>
              Delete
            </Button>
          </div>
        )
      },
    },
  ]

  return (
    <DashboardWrapper page="subscriptions-automation" title="Subscription Automation">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-gray-500">
          One shared daily sweep evaluates every enabled automation below — there's no separate
          per-automation schedule to configure beyond the trigger condition.
        </p>
        <Button onClick={() => history.push('/subscriptions/automation/new')}>Create Automation</Button>
      </div>
      {loadError && (
        <div className="bg-white border border-red-200 rounded-xl p-4 text-sm text-red-600 mb-2">
          Couldn't load automations: {loadError}{' '}
          <button type="button" className="underline" onClick={load}>
            Retry
          </button>
        </div>
      )}
      <BaseTable data={data} columns={columns} loading={loading} />
    </DashboardWrapper>
  )
}
